/**
 * What to tell a person when a request failed.
 *
 * The backend's `message` is shown when it reads like a sentence for a
 * person; HTTP status text, exception dumps and empty bodies are translated
 * into one plain line. Every error toast in the admin goes through here, so
 * the wording stays in one voice.
 */
export const GENERIC_ERROR = 'Something went wrong. Try again.';
export const NETWORK_ERROR = 'Could not reach the server. Check your connection and try again.';

const BY_STATUS: Record<number, string> = {
  401: 'Your session has expired. Sign in again.',
  403: "You don't have permission to do that.",
  404: 'That no longer exists.',
  408: 'The request took too long. Try again.',
  409: 'Someone else changed this in the meantime. Reload and try again.',
  413: 'That file is too large.',
  422: 'Some fields are not valid. Check the highlighted fields.',
  429: 'Too many requests. Wait a moment and try again.',
  500: GENERIC_ERROR,
  502: GENERIC_ERROR,
  503: 'The server is busy. Try again in a moment.',
  504: 'The request took too long. Try again.',
};

/** Bare status phrases a framework emits when nobody wrote a message. */
const STATUS_PHRASES = new Set(
  [
    'server error',
    'internal server error',
    'error',
    'bad request',
    'unauthorized',
    'unauthenticated',
    'unauthenticated.',
    'forbidden',
    'not found',
    'method not allowed',
    'unprocessable content',
    'unprocessable entity',
    'too many requests',
    'too many attempts.',
    'bad gateway',
    'service unavailable',
    'gateway timeout',
    'validation error',
    'the given data was invalid.',
  ].map((s) => s.toLowerCase())
);

/** Text that came from code, not from a person: never show it. */
const looksTechnical = (s: string): boolean =>
  STATUS_PHRASES.has(s.trim().toLowerCase()) ||
  /SQLSTATE|Exception|Undefined|Call to|stack|Trace|\bat line\b|syntax error|cURL|ECONN|ENOTFOUND|status code \d{3}|Request failed|Network Error|^\s*[{[]/i.test(
    s
  ) ||
  /^[A-Z_]{6,}$/.test(s.trim());

/** The first message inside a Laravel `errors` object, when there is one. */
const firstValidationMessage = (errors: unknown): string | undefined => {
  if (!errors || typeof errors !== 'object') return undefined;
  for (const value of Object.values(errors as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim()) return value;
    if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) return value[0];
    if (value && typeof value === 'object') {
      const nested = firstValidationMessage(value);
      if (nested) return nested;
    }
  }
  return undefined;
};

const usable = (s: unknown): s is string =>
  typeof s === 'string' && s.trim() !== '' && !looksTechnical(s);

/**
 * The line to show for a failed request. Works for an Axios error, a plain
 * Error, or anything else thrown; never returns an empty string.
 */
export const messageForError = (error: unknown, fallback: string = GENERIC_ERROR): string => {
  const e = error as {
    isAxiosError?: boolean;
    response?: { status?: number; data?: { message?: unknown; errors?: unknown } };
    message?: unknown;
    code?: string;
  } | null;
  if (!e || typeof e !== 'object') return fallback;

  if (e.isAxiosError) {
    const res = e.response;
    if (!res) return e.code === 'ECONNABORTED' ? BY_STATUS[408] : NETWORK_ERROR;
    const data = res.data ?? {};
    const status = res.status ?? 0;
    if (status === 422) {
      const first = firstValidationMessage(data.errors);
      if (usable(first)) return first;
    }
    if (usable(data.message)) return data.message;
    return BY_STATUS[status] ?? (status >= 500 ? GENERIC_ERROR : fallback);
  }

  return usable(e.message) ? e.message : fallback;
};

/**
 * True when the server understood the request and refused it (422) — e.g. a move
 * the order's current status no longer allows. The page is then likely stale.
 */
export const isRefusal = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 422;
